import { StandardError } from '@/types/api'

/**
 * Converts arrays in an object to comma-separated strings for URL parameters.
 * This is useful for APIs that expect array values as comma-separated strings.
 *
 * @param {T} params - Object containing parameters that may include arrays
 * @returns {Record<string, unknown>} Object with arrays converted to comma-separated strings
 * @example
 * const params = { ids: [1, 2, 3], name: 'test' }
 * const serialized = serializeArrayParams(params)
 * // Result: { ids: '1,2,3', name: 'test' }
 */
export const serializeArrayParams = <T>(params: T): Record<string, unknown> => {
  const serialized: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(
    params as Record<string, unknown>
  )) {
    if (Array.isArray(value)) {
      serialized[key] = value.join(',')
    } else {
      serialized[key] = value
    }
  }

  return serialized
}

interface TusErrorResponse {
  getStatus: () => number
  getBody: () => string
}

/**
 * Resposta HTTP de um erro do tus-js-client (`DetailedError`), se houver.
 */
const getTusErrorResponse = (error: unknown): TusErrorResponse | null => {
  if (error && typeof error === 'object' && 'originalResponse' in error) {
    const response = (error as { originalResponse: unknown }).originalResponse
    if (
      response &&
      typeof (response as TusErrorResponse).getStatus === 'function'
    ) {
      return response as TusErrorResponse
    }
  }
  return null
}

/**
 * Status HTTP de um erro do axios ou do tus-js-client.
 */
export const getErrorStatus = (error: unknown): number | undefined => {
  const tusResponse = getTusErrorResponse(error)
  if (tusResponse) {
    return tusResponse.getStatus()
  }
  if (error && typeof error === 'object' && 'response' in error) {
    return (error as { response?: { status?: number } }).response?.status
  }
  return undefined
}

/**
 * 429: a API recusou por limite de requisições (rate limit).
 */
export const isThrottledError = (error: unknown): boolean =>
  getErrorStatus(error) === 429

/**
 * Returns the error message from a catch error.
 * If it's an AxiosError with StandardError in response.data, returns the first detail from errors.
 * Erros do tus-js-client: lê o mesmo formato no corpo da resposta.
 * Otherwise, returns the provided default message.
 *
 * @param {unknown} error - The caught error (can be any type)
 * @param {string} [defaultMessage='Ocorreu um erro inesperado'] - Default message to display if not a StandardError or has no detail
 * @returns {string} The extracted error message or default message
 * @example
 * const message = getErrorMessage(error, 'Error processing request');
 */
export const getErrorMessage = (
  error: unknown,
  defaultMessage = 'Ops! Ocorreu um erro inesperado. Por favor, tente novamente mais tarde.'
): string => {
  const tusResponse = getTusErrorResponse(error)
  if (tusResponse) {
    try {
      return getErrorMessage(JSON.parse(tusResponse.getBody()), defaultMessage)
    } catch {
      // Corpo fora do formato da API (ex.: HTML de um proxy).
      return defaultMessage
    }
  }

  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'data' in error.response
  ) {
    const response = error.response as { data: unknown }
    const responseData = response.data

    if (
      responseData &&
      typeof responseData === 'object' &&
      'type' in responseData &&
      'errors' in responseData
    ) {
      const standardError = responseData as StandardError

      if (
        standardError.errors &&
        Array.isArray(standardError.errors) &&
        standardError.errors.length > 0 &&
        standardError.errors[0].detail
      ) {
        return standardError.errors[0].detail
      }
    }
  }

  if (
    error &&
    typeof error === 'object' &&
    'type' in error &&
    'errors' in error
  ) {
    const standardError = error as StandardError

    if (
      standardError.errors &&
      Array.isArray(standardError.errors) &&
      standardError.errors.length > 0 &&
      standardError.errors[0].detail
    ) {
      return standardError.errors[0].detail
    }
  }

  return defaultMessage
}
